import { Spinner } from "@photo-booth/ui/spinner";
import { Toaster } from "@photo-booth/ui/sonner";
import { Outlet, useNavigation, useLocation, useSearchParams } from "react-router";
import { AnimatePresence } from "framer-motion";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useMediaQuery } from "usehooks-ts";
import { usePageView } from "@/lib/analytics/usePageView";
import { useAllowedUserAgent } from "@/hooks/useAllowedUserAgent";
import { UnsupportedBrowserWarning } from "@/components/UnsupportedBrowserWarning";
import { BackToCameraReminderBanner } from "@/components/BackToCameraReminderBanner";
import { useEffect } from "react";

const queryClient = new QueryClient()
const MAINTENANCE_BYPASS_KEY = "maintenance-mode-bypass";

export const Layout = () => {
  const { state } = useNavigation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isMobile = useMediaQuery('(max-width: 768px)')
  const { isAllowed } = useAllowedUserAgent();

  usePageView();

  // Check if bypass flag is in query params and store it in sessionStorage
  useEffect(() => {
    const bypassFromQuery = searchParams.get("shouldByPassMaintenanceMode") === "true";
    if (bypassFromQuery) {
      sessionStorage.setItem(MAINTENANCE_BYPASS_KEY, "true");
    }
  }, [searchParams]);

  // Check both query params and sessionStorage for bypass flag
  const shouldByPassMaintenanceMode = 
    searchParams.get("shouldByPassMaintenanceMode") === "true" ||
    sessionStorage.getItem(MAINTENANCE_BYPASS_KEY) === "true";

  if (import.meta.env.VITE_MAINTAINANCE_MODE === "true" && !shouldByPassMaintenanceMode) {
    return (
      <div className="flex justify-center items-center w-full h-screen">
        <h1 className="text-4xl text-vintage-red text-center">We are currently performing maintenance. Please try again later.</h1>
      </div>
    )
  }

  if (state === "loading") {
    return (
      <>
        <div className="flex justify-center items-center w-full h-[80vh]">
          <Spinner />
        </div>
        <Toaster />
      </>
    );
  }

  if (!isMobile) {
    return (
      <div className="flex justify-center items-center w-full h-screen">
        <h1 className="text-4xl text-vintage-red text-center">This feature is only available on mobile devices. <br /> Please use a mobile device to continue.</h1>
      </div>
    )
  }

  if (!isAllowed) {
    return <UnsupportedBrowserWarning />
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AnimatePresence mode="wait" initial={false}>
        <div key={location.pathname}>
          <Outlet />
        </div>
      </AnimatePresence>
      <BackToCameraReminderBanner />
      <Toaster />
    </QueryClientProvider>
  );
};
